/**
 * /timeline — everything that happened in a week, in one column.
 *
 * The events are computed server-side from the tables that already hold them
 * (see `routes/timeline.ts`); this screen is a week switcher and a list. The
 * arrows step over weeks an admin has switched off.
 *
 * `?week=` lives in the URL rather than in component state so that a row
 * linking out to a game and the back button that follows land on the week the
 * reader was actually looking at.
 */

import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { TimelinePage } from '@shared/types'
import { ApiFailure, GET } from '../lib/api'
import { Body, Header, Notice, Screen, SkeletonCard, Spacer } from '../ui'
import { TimelineList } from '../ui/TimelineList'
import { WeekSwitcher } from '../ui/WeekSwitcher'

export default function Timeline() {
  const [params, setParams] = useSearchParams()
  const weekParam = params.get('week')

  const [page, setPage] = useState<TimelinePage | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const now = Date.now()

  useEffect(() => {
    const ac = new AbortController()
    void (async () => {
      try {
        const qs = weekParam ? `?week=${encodeURIComponent(weekParam)}` : ''
        setPage(await GET<TimelinePage>(`/api/timeline${qs}`, ac.signal))
        setErr(null)
      } catch (e) {
        if (ac.signal.aborted) return
        // Last-known-good stays on screen (D103).
        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t load the timeline.')
      }
    })()
    return () => ac.abort()
  }, [weekParam])

  const onWeek = useCallback((id: string) => {
    setPage(null)
    setParams({ week: id }, { replace: true })
  }, [setParams])

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        title={<span className="screen-title">🧾 Timeline</span>}
        sub={
          <div style={{ marginTop: 10 }}>
            <WeekSwitcher
              weeks={page?.weeks ?? []}
              weekId={page?.weekId ?? null}
              onChange={onWeek}
              label={page?.weekLabel}
            />
          </div>
        }
      />

      <Body style={{ padding: '8px var(--gutter) calc(24px + var(--nav-h))' }}>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

        {!page ? (
          <><SkeletonCard lines={3} /><Spacer h={12} /><SkeletonCard lines={4} /><Spacer h={12} /><SkeletonCard lines={3} /></>
        ) : page.events.length === 0 ? (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Nothing has happened yet.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6, lineHeight: 1.5 }}>
              Scores, shouts, feats, check-ins and chip-ins all land here as they happen.
            </div>
          </div>
        ) : (
          <TimelineList events={page.events} now={now} />
        )}
      </Body>
    </Screen>
  )
}
